import { useState } from 'react';
import Page from './Page';
import { useHistory, useParams } from 'react-router-dom';
import { useAppSelector, useAppDispatch } from 'flow/hooks/useRedux';
import Toggle from '../ui-components/Toggle';
import SettingRow from './SettingRow';
import {
    getDefaultNotificationSetting,
    updateProfileNotificationType,
    updatePushTypeSetting,
} from 'flow/utils/notification-utils';
import { getPushtypes, updatePushtypes } from 'flow/actions/extension-actions';
import { routes } from 'flow/types/routes';

import { Profile } from 'flow/types/Profile';
import { TypedMap } from 'flow/types/TypedMap';
import { NotificationSetting } from 'flow/types/Extension';
import useProfiles from 'flow/hooks/useProfiles';
import Loading from 'flow/components/ui-components/Loading';
import { generatePath } from 'flow/utils/router-utils';
import { ExtensionKey } from 'flow/types/EntityKeys';

interface RouteParams {
    pushType: string;
    device: 'mobile' | 'sms' | 'desktop' | 'email';
    extensionKey: ExtensionKey;
}

const NotificationProfiles = () => {
    const dispatch = useAppDispatch();
    const history = useHistory();
    const { pushType, device, extensionKey } = useParams<RouteParams>();

    const [disabledAllToggles, setDisabledAllToggles] = useState(false);

    const { sortedProfiles } = useProfiles(extensionKey);

    const pushTypes = useAppSelector((state) =>
        state.extensions.getIn(['extensions', extensionKey, 'pushTypes']),
    );

    if (!sortedProfiles || !pushTypes) {
        return <Loading />;
    }

    const pushTypeSetting = pushTypes.find(
        (setting) => setting.get('name') === pushType,
    );

    const handleToggle = async (
        key: string,
        value: boolean,
        currentSetting: TypedMap<NotificationSetting>,
    ) => {
        const updatedPushType = updateProfileNotificationType(
            pushTypeSetting,
            key,
            device,
            !value,
        );

        const updatedPushTypes = updatePushTypeSetting(
            pushTypes,
            updatedPushType,
            currentSetting,
        );

        setDisabledAllToggles(true);
        await dispatch(updatePushtypes(extensionKey, updatedPushTypes));
        await dispatch(getPushtypes(extensionKey));
        setDisabledAllToggles(false);
    };

    const renderProfile = (profileDTO: TypedMap<Profile>) => {
        const key = profileDTO.get('key');
        const currentSetting = pushTypeSetting
            .get('notificationSettings')
            .find((setting) => setting.getIn(['profile', 'key']) === key);

        const defaultSetting = getDefaultNotificationSetting(
            pushTypeSetting.get('notificationSettings'),
        );

        const value = currentSetting?.has(device)
            ? currentSetting.get(device)
            : defaultSetting.get(device);
        return (
            <SettingRow
                key={`${key}-settingRow`}
                cta={
                    <Toggle
                        value={value}
                        onChange={() =>
                            handleToggle(key, value, currentSetting)
                        }
                        disabled={disabledAllToggles}
                        text={profileDTO.get('description')}
                    />
                }
            >
                {profileDTO.get('description')}
            </SettingRow>
        );
    };

    return (
        <Page
            header={`settings.${pushType}`}
            onClickBack={() =>
                history.push(
                    `${generatePath(routes['settings.notifications'], { extensionKey })}?type=${device}`,
                )
            }
        >
            {sortedProfiles?.map((profileDTO) => renderProfile(profileDTO))}
        </Page>
    );
};

export default NotificationProfiles;
